import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, RHFAutocomplete, enqueueSnackbar } from '@zeta-core';
import { AdvisorAPIs, getErrorMessage } from '@zeta-services';
import {
  IAddAdvisorUserRequest,
  IAdvisorUser,
  IEmptyRequestResponse,
  IGenericErrorResponse,
  IGetAdvisorUserListRequest,
  IGetAdvisorUserListResponse,
} from '@zeta-types';
import { AxiosResponse } from 'axios';
import { SyntheticEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router';
import * as yup from 'yup';
interface UserOption {
  label: string;
  value: string;
  email: string;
  advisorUserID: string;
}

export const AddUserAccessModal = ({
  open,
  onClose,
  setAdvisorUserList,
}: {
  open: boolean;
  onClose: () => void;
  setAdvisorUserList: (advisorUserList: IAdvisorUser[]) => void;
}) => {
  const location = useLocation();
  const caseID = location.state?.clientData?.caseID;
  const [searchText, setSearchText] = useState('');
  const [options, setOptions] = useState<UserOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<UserOption | null>(null);

  const methods = useForm<{ nameAndEmail: UserOption | null }>({
    defaultValues: {
      nameAndEmail: null,
    },
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(
      yup.object().shape({
        nameAndEmail: yup
          .object()
          .shape({
            label: yup.string().required('Please select an Advisor User'),
            value: yup.string().required('Please select an Advisor User'),
            advisorUserID: yup.string().required('Please select an Advisor User'),
          })
          .required('Please select an Advisor User'),
      })
    ),
  });

  const getAdvisorUserList = useMutation({
    mutationFn: (payload: IGetAdvisorUserListRequest) => AdvisorAPIs().getAdvisorUserList(payload),
    onSuccess: (response: AxiosResponse<IGetAdvisorUserListResponse>) => {
      const userList =
        response.data?.advisorUsers?.map((user) => ({
          label: `${user.fullName} (${user.email})`,
          value: user.email,
          email: user.email,
          advisorUserID: user.advisorUserID,
        })) || [];
      setOptions(userList);
    },
    onError: (error: IGenericErrorResponse) => {
      const errorResponse = error?.response?.data;
      const errorCode = errorResponse?.error?.errorCode || (Array.isArray(errorResponse?.error) && errorResponse?.error[0]?.errorCode);
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  const getAddedAdvisorUserList = useMutation({
    mutationFn: (payload: IGetAdvisorUserListRequest) => AdvisorAPIs().getAdvisorUserList(payload),
    onSuccess: (response: AxiosResponse<IGetAdvisorUserListResponse>) => {
      setAdvisorUserList(response?.data?.advisorUsers);
    },
    onError: (error: IGenericErrorResponse) => {
      console.log(error);
      const errorResponse = error?.response?.data;
      const errorCode = errorResponse?.error?.errorCode || (Array.isArray(errorResponse?.error) && errorResponse?.error[0]?.errorCode);
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  const addUserAccess = useMutation({
    mutationFn: (payload: IAddAdvisorUserRequest) => AdvisorAPIs().addAdvisorUser(payload),
    onSuccess: (response: AxiosResponse<IEmptyRequestResponse>) => {
      getAddedAdvisorUserList.mutate({ caseID });
      enqueueSnackbar('User access added successfully', { variant: 'success' });
      setOptions([]);
      setSearchText('');
      setSelectedOption(null);
      methods.reset();
      onClose();
    },
    onError: (error: IGenericErrorResponse) => {
      const errorResponse = error?.response?.data;
      const errorCode = errorResponse?.error?.errorCode || (Array.isArray(errorResponse?.error) && errorResponse?.error[0]?.errorCode);
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchText) {
        const payload: IGetAdvisorUserListRequest = {
          caseID: caseID,
          isAddingAccess: true,
          emailORNameFilter: searchText,
        };
        getAdvisorUserList.mutate(payload);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchText]);

  const handleInputChange = (_event: SyntheticEvent<Element, Event>, value: string) => {
    // Don't update search text if we've selected an option
    if (selectedOption && selectedOption.label === value) {
      return;
    }

    setSearchText(value);
    if (!value) {
      setOptions([]);
    }
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: UserOption | null) => {
    setSelectedOption(newValue);
    methods.setValue('nameAndEmail', newValue);

    // Clear search text when selecting an option
    if (newValue) {
      setSearchText('');
    }
  };

  const handleSubmit = (data: { nameAndEmail: UserOption | null }) => {
    console.log(data);
    const payload: IAddAdvisorUserRequest = {
      caseID: caseID,
      advisorUserID: data.nameAndEmail?.advisorUserID || '',
    };
    addUserAccess.mutate(payload);
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={() => {
        setOptions([]);
        setSearchText('');
        setSelectedOption(null);
        onClose();
      }}
    >
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(handleSubmit)}>
        <DialogTitle id="caution">Add Access</DialogTitle>

        <DialogContent>
          <Stack gap={2} mt={2}>
            <RHFAutocomplete<UserOption, false, false, false>
              name="nameAndEmail"
              label="Enter a name or work email"
              placeholder="Enter a name or work email"
              options={options}
              onInputChange={handleInputChange}
              onChange={handleChange}
              inputValue={searchText || methods.getValues('nameAndEmail')?.label}
              loading={getAdvisorUserList?.isPending}
              noOptionsText={searchText.length < 3 ? 'Type at least 3 characters to search' : 'No users found'}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={(option) => option?.label || ''}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setOptions([]);
              setSearchText('');
              setSelectedOption(null);
              methods.reset();
              onClose();
            }}
            id="cancel-button"
          >
            Cancel
          </Button>

          <LoadingButton variant="contained" id="delete-button" type="submit" loading={addUserAccess?.isPending}>
            Confirm
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};
